import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { Chart, registerables } from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import { forkJoin } from 'rxjs'
import { Subscription } from 'rxjs/internal/Subscription'
import { SlatGraphDto } from 'src/app/models/slat/slat-graph-dto'
import { SlatService } from 'src/app/services/slat/slat.service'

Chart.register(...registerables, annotationPlugin)

const skipped = (ctx: any, value: any) =>
  ctx.p0.skip || ctx.p1.skip ? value : undefined
const down = (ctx: any, value: any) =>
  ctx.p0.parsed.y > ctx.p1.parsed.y ? value : undefined

@Component({
  selector: 'app-slat-graph',
  templateUrl: './slat-graph.component.html',
  styleUrl: './slat-graph.component.scss',
  standalone: false,
})
export class SlatGraphComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('myChart') myChart!: ElementRef<HTMLCanvasElement>
  @Input() showLegend: boolean = true
  @Input() clientId: string
  @Input() showLimits: boolean = true
  @Input() visibleFilters: boolean = true
  @Input() visibleLegend: boolean = true
  @Input() visibleLimits: boolean = true
  chart: Chart
  routerSubscription!: Subscription

  slatGraphData: SlatGraphDto[] = []

  filteredData = this.slatGraphData
  uniqueTemplates: string[] = []
  uniqueIndicators: string[] = []
  selectedTemplates: string[] = []
  isLegend: boolean = this.showLegend
  isLimits: boolean = this.showLimits
  graphColors: { key: string; color: string }[] = []
  isComponentInitialized = false
  desiredOrder: string[] = [
    'CELF P-3 - Core Language Score',
    'CELF P-3 - Receptive Language Index',
    'CELF P-3 - Expressive Language Index',
    'CELF P-3 - Language Content Index',
    'CELF P-3 - Language Structure Index',
    'CELF P-3 - Academic Language Readiness Index',
    'CELF P-3 - Early Literacy Index',
  ]

  constructor(
    private router: Router,
    private slatService: SlatService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.initialiseChart()
  }

  initialiseData(graphData: SlatGraphDto[]) {
    graphData.sort(
      (a, b) => a.ageInMonthsAtAssessment - b.ageInMonthsAtAssessment,
    )
    this.slatGraphData = graphData
    this.filteredData = this.slatGraphData
    this.uniqueTemplates = this.getUniqueValues(this.slatGraphData, 'template')
    this.uniqueIndicators = this.getUniqueValues(
      this.slatGraphData,
      'indicator',
    )
    this.uniqueTemplates.forEach((element) => {
      this.selectedTemplates.push(element)
    })
  }

  initialiseChart() {
    this.isLegend = this.showLegend
    let request = {
      graphData: this.slatService.GetGraphData(this.clientId),
    }
    forkJoin(request).subscribe({
      next: ({ graphData }) => {
        this.initialiseData(graphData)
      },
      error: (err) => {
        throw err
      },
      complete: () => {
        this.translate
          .get([
            'CHART.X_AXIS',
            'CHART.Y_AXIS',
            'CHART.Upper_Limit',
            'CHART.Lower_Limit',
          ])
          .subscribe((translations: any) => {
            this.createChart(translations)
          })
      },
    })
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.refreshChart()
      }
    })
    this.translate.onLangChange.subscribe(() => {
      if (this.isComponentInitialized) {
        this.refreshChart()
      }
    })
  }

  ngAfterViewInit(): void {
    this.isComponentInitialized = true
    this.translate
      .get([
        'CHART.X_AXIS',
        'CHART.Y_AXIS',
        'CHART.Upper_Limit',
        'CHART.Lower_Limit',
      ])
      .subscribe((translations: any) => {
        this.createChart(translations)
      })
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy()
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe()
    }
  }

  createChart(translations: any) {
    if (this.chart) {
      this.chart.destroy()
    }
    this.chart = new Chart(this.myChart?.nativeElement, {
      type: 'line',
      data: {
        datasets: this.getDatasets(),
      },
      options: {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        scales: {
          x: {
            type: 'linear',
            position: 'bottom',
            title: {
              display: true,
              text: translations['CHART.X_AXIS'],
            },
          },
          y: {
            title: {
              display: true,
              text: translations['CHART.Y_AXIS'],
            },
            max: 160,
            ticks: {
              stepSize: 15,
            },
          },
        },
        plugins: {
          legend: {
            display: this.isLegend,
            position: 'right',
            align: 'start',
            labels: {
              font: {
                size: 10,
              },
              usePointStyle: true,
              pointStyle: 'circle',
              sort: (a: any, b: any) => {
                return (
                  this.desiredOrder.indexOf(a.text) -
                  this.desiredOrder.indexOf(b.text)
                )
              },
            },
          },
          annotation: {
            annotations: {
              upperLimit: {
                type: 'line',
                yMin: 115,
                yMax: 115,
                borderColor: 'grey',
                borderWidth: 2,
                borderDash: [3, 3],
                label: {
                  font: {
                    size: 9,
                  },
                  content: translations['CHART.Upper_Limit'],
                  display: true,
                  position: 'end',
                  backgroundColor: 'rgba(255, 0, 0, 0.5)',
                },
                display: this.isLimits,
              },
              lowerLimit: {
                type: 'line',
                yMin: 85,
                yMax: 85,
                borderColor: 'grey',
                borderWidth: 2,
                borderDash: [3, 3],
                label: {
                  font: {
                    size: 9,
                  },
                  content: translations['CHART.Lower_Limit'],
                  display: true,
                  position: 'end',
                  backgroundColor: 'rgba(255, 0, 0, 0.5)',
                },
                display: this.isLimits,
              },
            },
          },
        },
      },
    })
  }

  getDatasets() {
    const groupedData = this.groupDataByTemplateAndIndicator()
    const graphData = Object.keys(groupedData).map((key) => ({
      label: key,
      data: groupedData[key],
      fill: false,
      borderColor: this.getRandomColor(key),
      pointBorderColor: this.getRandomColor(key),
      backgroundColor: this.getRandomColor(key),
      pointRadius: 5,
      pointHoverRadius: 10,
      segment: {
        borderColor: (ctx: any) =>
          skipped(ctx, this.getRandomColor(key)) ||
          down(ctx, this.getRandomColor(key)),
        borderDash: (ctx: any) => skipped(ctx, [6, 6]),
      },
      spanGaps: true,
    }))
    return graphData
  }

  groupDataByTemplateAndIndicator(): {
    [key: string]: { x: number; y: number }[]
  } {
    return this.filteredData.reduce(
      (acc: { [key: string]: { x: number; y: number }[] }, item) => {
        const key = `${item.template} - ${item.indicator}`
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push({ x: item.ageInMonthsAtAssessment, y: item.score })
        return acc
      },
      {},
    )
  }

  getRandomColor(key: string) {
    const letters = '0123456789ABCDEF'
    let findColor = this.graphColors.find((item) => item.key === key)?.color
    if (!findColor) {
      findColor = '#'
      for (let i = 0; i < 6; i++) {
        findColor += letters[Math.floor(Math.random() * 16)]
      }
      this.graphColors.push({ key, color: findColor })
    }
    return findColor
  }

  toggleTemplateSelection(template: string) {
    const index = this.selectedTemplates.indexOf(template)
    if (index >= 0) {
      this.selectedTemplates.splice(index, 1)
    } else {
      this.selectedTemplates.push(template)
    }
    this.filterData()
  }

  filterData() {
    this.filteredData = this.slatGraphData.filter((item) =>
      this.selectedTemplates.includes(item.template),
    )
    this.chart.data.datasets = this.getDatasets()
    this.chart.update()
  }

  getUniqueValues(data: any[], key: string): string[] {
    return [...new Set(data.map((item) => item[key]))]
  }

  refreshChart() {
    this.chart.destroy()
    this.translate
      .get([
        'CHART.X_AXIS',
        'CHART.Y_AXIS',
        'CHART.Upper_Limit',
        'CHART.Lower_Limit',
      ])
      .subscribe((translations: any) => {
        this.createChart(translations)
      })
  }
}
